import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { token } from '../../utils/theme';
interface SearchZoneMapProps {
  center: [number, number];
  /** Rayon de diffusion en kilomètres. */
  radiusKm: number;
  label: string;
}

/** Fond de carte avec le cercle de la zone de recherche d'un animal perdu. */
export function SearchZoneMap({
  center,
  radiusKm,
  label
}: SearchZoneMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const pointRef = useRef<L.CircleMarker | null>(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      scrollWheelZoom: false,
      dragging: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false
    }).setView(center, 14);
    map.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    mapRef.current = map;
    // Le dialogue s'anime à l'ouverture : on recalcule la taille une fois en place.
    const timer = window.setTimeout(() => map.invalidateSize(), 260);
    return () => {
      window.clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      circleRef.current = null;
      pointRef.current = null;
    };
  }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const meters = radiusKm * 1000;
    const accent = token('--ville-ochre', '#1f7fae');
    if (circleRef.current) {
      circleRef.current.setLatLng(center).setRadius(meters);
    } else {
      circleRef.current = L.circle(center, {
        radius: meters,
        color: accent,
        weight: 2,
        fillColor: accent,
        fillOpacity: 0.12
      }).addTo(map);
    }
    if (pointRef.current) {
      pointRef.current.setLatLng(center).setTooltipContent(label);
    } else {
      pointRef.current = L.circleMarker(center, {
        radius: 7,
        color: '#ffffff',
        weight: 3,
        fillColor: accent,
        fillOpacity: 1
      }).bindTooltip(label, {
        direction: 'top',
        offset: [0, -8]
      }).addTo(map);
    }
    // Bornes calculées à partir du centre : `circle.getBounds()` exige que le cercle soit déjà
    // rattaché à une carte initialisée, ce qui n'est pas le cas au premier rendu.
    map.fitBounds(L.latLng(center).toBounds(meters * 2), {
      padding: [12, 12],
      animate: false
    });
  }, [center, radiusKm, label]);
  return <div ref={containerRef} role="img" aria-label={`Zone de recherche de ${radiusKm.toFixed(1)} km autour de ${label}`} className="h-52 w-full bg-ville-mist" data-id="element-262" data-mp="2pml64qeeh4zk" />;
}